import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoService } from '@jsverse/transloco';
import * as _ from 'lodash';
import { NgxSpinnerService } from 'ngx-spinner';
import { TasksUtils } from 'projects/shared/tasks.utils';
import { map } from 'rxjs/operators';
import { ApplicationDetailModel } from '../../models/borrower-portal/application-detail-model';
import { ApplicationForUser } from "../../models/borrower-portal/application-for-user-model";
import { ApplicationTaskModel, BorrowerTaskStatus, BorrowerTaskType } from "../../models/borrower-portal/application-task-model";
import { ApplicationTasksModel } from "../../models/borrower-portal/application-tasks-model";
import { BorrowerDocumentModel } from "../../models/borrower-portal/borrower-document-model";
import { FeatureFlags } from '../../models/feature-flags.model';
import { PricingScenario } from '../../models/pricing/pricing-scenario.model';
import { BasePortalService } from "../../services/base-portal.service";
import { Constants } from '../../services/constants';
import { MortgageApplicationService } from '../../services/mortgage-application.service';
import { NavigationService } from "../../services/navigation.service";
import { NotificationService } from '../../services/notification.service';
import { AddFilesToTaskDialogComponent } from './add-files-to-task-dialog/add-files-to-task-dialog.component';
import { AssetsVerificationDialogComponent } from './assets-verification-dialog/assets-verification-dialog.component';
import { AttachedDocumentsDialogComponent } from './attached-documents-dialog/attached-documents-dialog.component';
import { EConsentDialogComponent } from './e-consent-dialog/e-consent-dialog.component';
import { CreditAuthConsentDialogComponent } from './credit-auth-consent-dialog/credit-auth-consent-dialog.component';
import { PaymentInfoDialogComponent } from './payment-info-dialog/payment-info-dialog.component';
import { IncomeEmploymentVerificationDialogComponent } from './income-employment-verification-dialog/income-employment-verification-dialog.component';
import { TaskBorrowerPickerDialogComponent } from './task-borrower-picker-dialog/task-borrower-picker-dialog.component';
import { OtherBorrowersOnLoanModel } from '../../models/borrower-portal/other-borrowers-on-loan-model';
import { ApplicationContext } from '../../models/application-context.model';
import { ApplicationContextService } from '../../services/application-context.service';
import { WebsiteLinkDialogComponent } from './website-link-dialog/website-link-dialog.component';
import { localizeField } from '../../transloco/localize-field';

declare const Swal: any;

export interface TasksLoadedEvent {
  requested: ApplicationTaskModel[];
  all: ApplicationTaskModel[];
}

@Component({
    selector: 'portal-task-information',
    templateUrl: './task-information.component.html',
    styleUrls: ['task-information.component.scss'],
    standalone: false
})
export class PortalTaskInformationComponent implements OnInit {

  @Input()
  appId: number;

  @Input()
  isAgent: boolean = false;

  @Input()
  doNotEnforceChronologicalOrderInEsignTasks: boolean = false;

  @Output()
  tasksLoaded: EventEmitter<TasksLoadedEvent> = new EventEmitter<TasksLoadedEvent>();

  application: ApplicationForUser = new ApplicationForUser();
  applicationTasks: ApplicationTasksModel = new ApplicationTasksModel;
  filteredApplicationTasks: ApplicationTasksModel = new ApplicationTasksModel;
  selectedTab: number = 1;

  selectedApplicationTasks: ApplicationTaskModel[] = [];
  groupedTasks: ApplicationTaskModel[][] = [];

  /** Tasks organized by borrower for mobile grouped display */
  borrowerSections: { borrowerName: string; borrowerId: number; taskGroups: ApplicationTaskModel[][] }[] = [];
  hasMultipleBorrowers: boolean = false;

  documents: BorrowerDocumentModel[] = [];
  scenarios: PricingScenario[] = [];
  selectedBorrowerId = 'all';
  selectDisplayMode: "list" | "card" = "card";

  selectedTask: ApplicationTaskModel = new ApplicationTaskModel();
  eConsentText: string;
  creditAuthText: string;
  private _config: FeatureFlags | undefined;
  voaEnabled: boolean = false;
  voeEnabled: boolean = false;
  plaidCheckEnabled: boolean = false;

  isLoadingTasks: boolean = false;
  isLoadingDocs: boolean = false;
  isLoadingScenarios: boolean = false;

  losEsignTaskIdToSignFirst: number = null;

  otherBorrowersOnLoan: OtherBorrowersOnLoanModel[] = [];

  appContext: ApplicationContext;

  constructor(private readonly _portalService: BasePortalService,
    private readonly _onlineAppService: MortgageApplicationService,
    private readonly _modalService: NgbModal,
    private readonly _spinner: NgxSpinnerService,
    private readonly _notifsService: NotificationService,
    private readonly _navigationService: NavigationService,
    private readonly _router: Router,
    private readonly _route: ActivatedRoute,
    private readonly _ctx: ApplicationContextService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _translocoService: TranslocoService) {

    this._ctx.context.subscribe(contextData => {
      this.appContext = contextData;
    })
  }

  ngOnInit() {
    this.initialize();
  }

  public reloadTasks = (isAfterDocumentUpload: boolean, silent: boolean = false) => {
    this.initialize(isAfterDocumentUpload, silent);
  }

  public openDigitalVerificationDialog = (taskType: BorrowerTaskType) => {
    if (this.isAgent) {
      return;
    }

    const allTasks = this.applicationTasks.requested.concat(this.applicationTasks.submitted).concat(this.applicationTasks.approved);

    const verificationTasks = allTasks.filter(t => t.borrowerTaskType === taskType);
    if (verificationTasks.length === 0) {
      return;
    }
    verificationTasks.sort((a, b) => {
      return <any>new Date(b.submittedDate) - <any>new Date(a.submittedDate);
    });
    // Now, group the tasks above by borrowerId
    const groupedTasks = _.groupBy(verificationTasks, 'borrowerId');
    if (Object.keys(groupedTasks).length > 1) {
      const borrowersToPickFrom = [];
      for (const key in groupedTasks) {
        const borrowerId = Number(key);
        const borrowerName = groupedTasks[key][0].borrowerName;
        borrowersToPickFrom.push({ id: borrowerId, name: borrowerName });
      }

      const modalRef = this._modalService.open(TaskBorrowerPickerDialogComponent, Constants.modalOptions.large);
      modalRef.componentInstance.borrowers = borrowersToPickFrom;

      modalRef.result.then((borrowerId) => {
        const tasksForBorrower = groupedTasks[borrowerId];
        if (tasksForBorrower && tasksForBorrower.length > 0) {
          if (taskType === BorrowerTaskType.DigitalAssetVerification) {
            this.startAssetsVerification(tasksForBorrower[0]);
          } else if (taskType === BorrowerTaskType.DigitalIncomeEmploymentVerification) {
            this.startIncomeEmploymentVerification(tasksForBorrower[0]);
          }
        }
      }, err => { });
    } else {
      // Start the asset verification for the first task
      if (taskType === BorrowerTaskType.DigitalIncomeEmploymentVerification) {
        this.startIncomeEmploymentVerification(verificationTasks[0]);
      } else if (taskType === BorrowerTaskType.DigitalAssetVerification) {
        this.startAssetsVerification(verificationTasks[0]);
      }
    }
  }

  protected onSelectedBorrowerChanged() {
    // if (this.selectedBorrowerId === 'all') {
    //   Object.assign(this.filteredApplicationTasks, this.applicationTasks);
    // } else {
    //   this.filteredApplicationTasks.approved = this.applicationTasks.approved.filter(i => i.borrowerId === Number(this.selectedBorrowerId));
    //   this.filteredApplicationTasks.requested = this.applicationTasks.requested.filter(i => i.borrowerId === Number(this.selectedBorrowerId));
    //   this.filteredApplicationTasks.submitted = this.applicationTasks.submitted.filter(i => i.borrowerId === Number(this.selectedBorrowerId));
    // }

    this.setSelectedApplicationTasks(this.selectedTab, false);
  }

  protected setSelectedApplicationTasks(tabIndex: number, isAfterDocumentUpload: boolean = false) {
    this.selectedTab = tabIndex;

    if (tabIndex < 3) {
      this.loadTasks(isAfterDocumentUpload);
    } else if (tabIndex === 3) { // documents
      this.selectedApplicationTasks = [];
      this.groupedTasks = [];
      this.loadDocuments();
    } else if (tabIndex === 4) { // scenarios
      this.selectedApplicationTasks = [];
      this.groupedTasks = [];
      this.loadScenarios();
    }
  }

  getSelectedTaskName() {
    if (this.selectedTab === 1) {
      return 'Outstanding';
    }
    else if (this.selectedTab === 2) {
      return 'Other';
    }
    return '';
  }

  openAttachedDocuments = (task: ApplicationTaskModel) => {
    const modalRef = this._modalService.open(AttachedDocumentsDialogComponent, Constants.modalOptions.xlarge);
    modalRef.componentInstance.task = task;

    modalRef.result.then(() => {
    });
  }

  openAddFiles(task: ApplicationTaskModel) {
    const modalRef = this._modalService.open(AddFilesToTaskDialogComponent, Constants.modalOptions.large);
    modalRef.componentInstance.task = task;
    modalRef.componentInstance.applicationId = this.appId;
    const dialog = modalRef.componentInstance as AddFilesToTaskDialogComponent;

    modalRef.result.then(() => {
      this.initialize(true);
    }, () => {
      // Closed after a partial upload: refresh the tasks, but without the "Upload Complete" toast
      if (dialog.hasUploadedFiles) {
        this.initialize();
      }
    });
  }

  openPaymentInfo = (task: ApplicationTaskModel) => {
    if (this.isAgent) {
      return;
    }

    const modalRef = this._modalService.open(PaymentInfoDialogComponent, Constants.modalOptions.xlarge);
    modalRef.componentInstance.task = task;

    modalRef.result.then(() => {
      this.initialize();
    });
  }

  onTaskMarkedAsNotApplicable = (task: ApplicationTaskModel) => {
    this.initialize();
  }

  onBorrowerAnsweredAQuestion = (task: ApplicationTaskModel) => {
    this.initialize();
  }

  protected startIncomeEmploymentVerification = (task: ApplicationTaskModel) => {
    if (this.isAgent) {
      return;
    }

    const modalRef = this._modalService.open(IncomeEmploymentVerificationDialogComponent, Constants.modalOptions.verificationFullscreen);
    modalRef.componentInstance.task = task;
    modalRef.componentInstance.applicationId = this.appId;
    modalRef.componentInstance.voeEnabled = this.voeEnabled;
    modalRef.componentInstance.plaidCheckEnabled = this.plaidCheckEnabled;

    modalRef.result.then(() => {
      this.initialize();
    }, err => { });
  }

  protected startAssetsVerification = (task: ApplicationTaskModel) => {
    if (this.isAgent) {
      return;
    }

    const modalRef = this._modalService.open(AssetsVerificationDialogComponent, Constants.modalOptions.verificationFullscreen);
    modalRef.componentInstance.task = task;
    modalRef.componentInstance.applicationId = this.appId;
    modalRef.componentInstance.voaEnabled = this.voaEnabled;
    modalRef.componentInstance.plaidCheckEnabled = this.plaidCheckEnabled;

    modalRef.result.then(() => {
      this.initialize();
    }, err => { });
  }

  protected openCreditAuthConsent = (task: ApplicationTaskModel) => {
    this.creditAuthText = localizeField(this._config, 'creditAuthText', this._translocoService.getActiveLang()) ?? '';

    const modalRef = this._modalService.open(CreditAuthConsentDialogComponent, Constants.modalOptions.large);
    modalRef.componentInstance.loanId = this.appId;
    modalRef.componentInstance.creditAuthText = this.creditAuthText || '';

    modalRef.result.then((res: boolean) => {
      if (!res) {
        return alert('Credit authorization is required to proceed.');
      }

      this._spinner.show();

      this._portalService.updateCreditAuthTaskStatus(task.taskId, { creditAuthText: this.creditAuthText || '' })
        .subscribe({
          next: () => {
            this._spinner.hide();
            this._notifsService.showSuccess('Credit authorization submitted successfully.', 'Credit Authorization');
            this.initialize();
          },
          error: (error) => {
            console.error(error.error?.message ? error.error.message : 'Credit authorization failed');
            this._spinner.hide();
            this._notifsService.showError('Credit authorization failed. Please try again.', 'Credit Authorization');
          }
        });
    }, err => { });
  }

  openEsignDocument = (task: ApplicationTaskModel) => {
    const { applicationId, myDetails, } = this.application;

    // call api to see if user assigned to task has e-consent completed if not, show econsent dialog, once completed, proceed to esign
    // if they do not econsent, toast a message that says, you must econsent to proceed with esigning your documents
    // if user already has econsent, just let them sign.

    if (task.borrowerId > 0) {
      if (task.borrowerId === myDetails?.borrowerId && myDetails?.acceptedEConsentDate) {
        return this.openSignerView(task);
      } else if (this.otherBorrowersOnLoan.length > 0) {
        const matchingBorrower = this.otherBorrowersOnLoan.find(ob =>
          task.borrowerId === ob.borrowerId && ob.acceptedEConsentDate
        );
        if (matchingBorrower) {
          return this.openSignerView(task);
        }
      }

      this.eConsentText = localizeField(this._config, 'eConsentText', this._translocoService.getActiveLang()) ?? '';

      const modalRef = this._modalService.open(EConsentDialogComponent, Constants.modalOptions.large);
      modalRef.componentInstance.loanId = this.appId;
      modalRef.componentInstance.eConsentText = this.eConsentText;

      modalRef.result.then((res: boolean) => {
        if (!res) {
          return alert('E-Consent authorization is required for e-signing your documents.');
        }

        this._spinner.show();

        this._portalService.acceptESignConsent(applicationId, task.borrowerId)
          .subscribe({
            next: (acceptConsentRes) => {

              this._onlineAppService.generateEConsentDocument(applicationId, { eConsentText: this.eConsentText, borrowerId: task.borrowerId })
                .subscribe({
                  next: (generateDocRes) => {
                    this.openSignerView(task)
                  },
                  error: (error) => {
                    console.error(error.error.message ? error.error.message : 'Document generation failed');
                    this._spinner.hide();
                  }
                })
            },
            error: (error) => {
              console.error(error.error.message ? error.error.message : 'Accept E-Sign Consent failed');
              this._spinner.hide();
            }
          });
      });

    } else {
      this.openSignerView(task);
    }
  }

  openWebsiteLinkModal = (task: ApplicationTaskModel) => {

    const modalRef = this._modalService.open(WebsiteLinkDialogComponent, Constants.modalOptions.xlarge);
    modalRef.componentInstance.task = task;
    modalRef.componentInstance.applicationId = this.appId;


    modalRef.result.then(() => {
      this._spinner.show();
      this._portalService.completeWebsiteLinkTask(task.taskId).subscribe({
        complete: () => this.initialize()
      });
    }).catch(() => {
    });
  }

  private initialize = (isAfterDocumentUpload: boolean = false, silent: boolean = false) => {
    const appID = this._route.snapshot.queryParamMap.get('applicationId');
    if (appID && !isNaN(Number(appID))) {
      this.appId = Number(appID);
    }
    this.loadLoanData(silent);
    this.getLoanConfigs(silent);
    this.loadDocuments(false);

    if (!this.isAgent) {
      this.loadScenarios(false);
    }

    this.setSelectedApplicationTasks(this.selectedTab, isAfterDocumentUpload);
  }

  private loadLoanData = (silent: boolean = false) => {
    if (!silent) {
      this._spinner.show();
    }
    this._portalService.getLoanDataForAppId(this.appId).subscribe({
      next: (application) => {
        if (!application.myDetails) {
          application.myDetails = new ApplicationDetailModel();
        }
        this.application = application;
        this.otherBorrowersOnLoan = application.otherBorrowersOnLoan.filter(o => o.isPortalMerged)

        // DEV MOCK: inject a fake co-borrower so borrower grouping is visible
        if (this.INJECT_MOCK_TASKS && this.otherBorrowersOnLoan.length === 0) {
          this.otherBorrowersOnLoan = [{
            borrowerId: 88888,
            borrowerName: 'Jane Tester',
            borrowerPhone: '555-555-1234',
            borrowerEmail: 'jane.tester@example.com',
            losPortalUserStatus: undefined,
            acceptedEConsentDate: undefined,
            isPortalMerged: true,
          }];
        }

        this._cdr.detectChanges();
      },
      error: (err) => {
        this._spinner.hide();
        this._cdr.detectChanges();
        this._notifsService.showError(err && err.error ? err.error.message || err.message : "An error occurred while getting loan details.", 'Error!');
      }
    });
  }

  private loadDocuments = (isLoadingShowing: boolean = true) => {
    if (isLoadingShowing) {
      this.isLoadingDocs = true;
    }

    this._portalService.getDocumentsForAppId(this.appId).subscribe(documents => {
      this.documents = documents;

      // Inject mock documents when dev flag is enabled
      if (this.INJECT_MOCK_TASKS) {
        this.documents = [...this.documents, ...this.getMockDocuments()];
      }

      this.documents.sort((a, b) => {
        return <any>new Date(b.dateInserted) - <any>new Date(a.dateInserted);
      });

      if (isLoadingShowing) {
        this.isLoadingDocs = false;
      }
      this._cdr.detectChanges();
    }, err => {
      if (isLoadingShowing) {
        this.isLoadingDocs = false;
      }
      this._cdr.detectChanges();
      this._notifsService.showError(err && err.error ? err.error.message || err.message : "An error occurred while getting loan documents.", 'Error!');
    });
  }

  /**
   * Dev-only flag: set to true to inject mock tasks for every BorrowerTaskType
   * so every task card can be visually verified on mobile.
   * This should be set to false before merging to production.
   */
  private readonly INJECT_MOCK_TASKS: boolean = false;

  private loadTasks = (isAfterDocumentUpload: boolean = false) => {
    this.isLoadingTasks = true;
    this._portalService.getTasksForAppId(this.appId)
      .pipe(
        map(res => {
          // TODO Remove After Backend Update For allowUpload released
          return {
            requested: res.requested
              ? res.requested.map(t => ({ ...t, allowUpload: this.allowUpload(t) }))
              : [],
            submitted: res.submitted
              ? res.submitted.map(t => ({ ...t, allowUpload: this.allowUpload(t) }))
              : [],
            approved: res.approved
              ? res.approved.map(t => ({ allowUpload: false, ...t }))
              : [],
          };
        })
      )
      .subscribe(applicationTasks => {

        this.isLoadingTasks = false;

        // Inject mock tasks for all BorrowerTaskTypes when dev flag is enabled
        if (this.INJECT_MOCK_TASKS) {
          applicationTasks.requested = [
            ...applicationTasks.requested,
            ...this.getMockTasksForAllTypes()
          ];
          // Also inject mock submitted tasks for both borrowers
          applicationTasks.submitted = [
            ...applicationTasks.submitted,
            ...this.getMockSubmittedTasksForMultipleBorrowers()
          ];
        }

        const loanStatus = this.appContext.globalConfig.loanStatus.find(w => w.loanStatusId == this.application.loanStatusId)

        this.applicationTasks = applicationTasks;

        if (loanStatus && loanStatus.hideFromCounter) {
          this.filterTaskWithLoanTaskStatus(isAfterDocumentUpload)
        } else {
          this.fillApplicationTasks(isAfterDocumentUpload)
        }
        this._cdr.detectChanges();

      }, err => {
        this.isLoadingTasks = false;
        this._cdr.detectChanges();
        this._notifsService.showError(err && err.error ? err.error.message || err.message : "An error occurred while getting loan tasks.", 'Error!');
      });
  }

  /**
   * Generates mock tasks for every BorrowerTaskType to verify mobile layout.
   * Each task has realistic field values matching what Elastic logs show.
   */
  private getMockTasksForAllTypes(): ApplicationTaskModel[] {
    const baseBorrowerId = this.application?.myDetails?.borrowerId || 99999;
    const baseBorrowerName = this.application?.myDetails?.borrowerName || 'John Smith';
    const now = new Date();
    const dueInDays = (days: number) => {
      const d = new Date(now);
      d.setDate(d.getDate() + days);
      return d;
    };

    return [
      // DigitalAssetVerification
      {
        taskId: 900001,
        taskTypeId: 'mock-asset-verification',
        borrowerTaskType: BorrowerTaskType.DigitalAssetVerification,
        description: 'Verify your bank accounts and assets digitally',
        docType: 'Asset Verification',
        docSortOrder: 1,
        dueDate: dueInDays(5),
        borrowerId: baseBorrowerId,
        borrowerName: baseBorrowerName,
        borrowerTaskStatus: BorrowerTaskStatus.Pending,
        linkedDocuments: [],
        allowUpload: false,
        isAgentTask: false,
        allowMarkNA: false,
        signingLinkSentInEmail: null,
        alwaysShowPending: false,
      } as ApplicationTaskModel,

      // DigitalIncomeEmploymentVerification
      {
        taskId: 900002,
        taskTypeId: 'mock-income-verification',
        borrowerTaskType: BorrowerTaskType.DigitalIncomeEmploymentVerification,
        description: 'Verify your employment and income details',
        docType: 'Income / Employment Verification',
        docSortOrder: 2,
        dueDate: dueInDays(7),
        borrowerId: baseBorrowerId,
        borrowerName: baseBorrowerName,
        borrowerTaskStatus: BorrowerTaskStatus.Pending,
        linkedDocuments: [],
        allowUpload: false,
        isAgentTask: false,
        allowMarkNA: false,
        signingLinkSentInEmail: null,
        alwaysShowPending: false,
      } as ApplicationTaskModel,

      // WebsiteLink
      {
        taskId: 900003,
        taskTypeId: 'mock-website-link',
        borrowerTaskType: BorrowerTaskType.WebsiteLink,
        description: 'Complete the homebuyer education course',
        docType: 'Homebuyer Education Course',
        docSortOrder: 3,
        dueDate: dueInDays(10),
        borrowerId: baseBorrowerId,
        borrowerName: baseBorrowerName,
        borrowerTaskStatus: BorrowerTaskStatus.Pending,
        linkedDocuments: [],
        allowUpload: false,
        isAgentTask: false,
        allowMarkNA: false,
        signingLinkSentInEmail: null,
        websiteUrl: 'https://example.com/homebuyer-education',
        alwaysShowPending: false,
      } as ApplicationTaskModel,

      // RequestInformation (Answer a Question)
      {
        taskId: 900004,
        taskTypeId: 'mock-request-info',
        borrowerTaskType: BorrowerTaskType.RequestInformation,
        description: 'Please provide your current rental payment amount',
        docType: 'Rental Payment Information',
        docSortOrder: 4,
        dueDate: dueInDays(3),
        borrowerId: baseBorrowerId,
        borrowerName: baseBorrowerName,
        borrowerFacingNote: 'We need your current monthly rental payment to proceed with underwriting.',
        borrowerTaskStatus: BorrowerTaskStatus.Pending,
        linkedDocuments: [],
        allowUpload: true,
        isAgentTask: false,
        allowMarkNA: false,
        signingLinkSentInEmail: null,
        alwaysShowPending: false,
      } as ApplicationTaskModel,

      // RequestHoi
      {
        taskId: 900005,
        taskTypeId: 'mock-request-hoi',
        borrowerTaskType: BorrowerTaskType.RequestHoi,
        description: 'Please provide your homeowners insurance information',
        docType: 'Homeowners Insurance',
        docSortOrder: 5,
        dueDate: dueInDays(14),
        borrowerId: baseBorrowerId,
        borrowerName: baseBorrowerName,
        borrowerTaskStatus: BorrowerTaskStatus.Pending,
        linkedDocuments: [],
        allowUpload: false,
        isAgentTask: false,
        allowMarkNA: false,
        signingLinkSentInEmail: null,
        alwaysShowPending: false,
      } as ApplicationTaskModel,

      // LosEsign
      {
        taskId: 900006,
        taskTypeId: 'mock-los-esign',
        borrowerTaskType: BorrowerTaskType.LosEsign,
        description: 'Sign your Initial Disclosure package',
        docType: 'Initial Disclosure Package',
        docSortOrder: 0,
        dueDate: dueInDays(2),
        borrowerId: baseBorrowerId,
        borrowerName: baseBorrowerName,
        borrowerFacingNote: 'Please review and sign this document at your earliest convenience.',
        borrowerTaskStatus: BorrowerTaskStatus.Pending,
        linkedDocuments: [{ fileGuid: 'mock-doc-1', fileName: 'InitialDisclosure.pdf', extension: 'pdf', dateInserted: new Date() }] as any[],
        allowUpload: false,
        isAgentTask: false,
        allowMarkNA: false,
        signingLinkSentInEmail: false,
        alwaysShowPending: false,
      } as ApplicationTaskModel,

      // RequestDocument (normal task with upload + not-applicable)
      {
        taskId: 900007,
        taskTypeId: 'mock-request-doc',
        borrowerTaskType: BorrowerTaskType.RequestDocument,
        description: 'Please upload your most recent 2 months of bank statements',
        docType: 'Bank Statements (2 months)',
        docSortOrder: 6,
        dueDate: dueInDays(5),
        borrowerId: baseBorrowerId,
        borrowerName: baseBorrowerName,
        borrowerFacingNote: 'All pages required, including blank pages. Must show full account number.',
        borrowerTaskStatus: BorrowerTaskStatus.Pending,
        linkedDocuments: [],
        allowUpload: true,
        isAgentTask: false,
        allowMarkNA: true,
        signingLinkSentInEmail: null,
        alwaysShowPending: false,
      } as ApplicationTaskModel,

      // GatherPayment
      {
        taskId: 900008,
        taskTypeId: 'mock-gather-payment',
        borrowerTaskType: BorrowerTaskType.GatherPayment,
        description: 'Please provide payment for appraisal fee',
        docType: 'Appraisal Fee Payment',
        docSortOrder: 7,
        dueDate: dueInDays(3),
        borrowerId: baseBorrowerId,
        borrowerName: baseBorrowerName,
        borrowerTaskStatus: BorrowerTaskStatus.Pending,
        linkedDocuments: [],
        allowUpload: false,
        isAgentTask: false,
        allowMarkNA: false,
        signingLinkSentInEmail: null,
        alwaysShowPending: false,
      } as ApplicationTaskModel,

      // CreditAuthorization
      {
        taskId: 900009,
        taskTypeId: 'mock-credit-auth',
        borrowerTaskType: BorrowerTaskType.CreditAuthorization,
        description: 'Authorize credit check for loan processing',
        docType: 'Credit Authorization',
        docSortOrder: 8,
        dueDate: dueInDays(1),
        borrowerId: baseBorrowerId,
        borrowerName: baseBorrowerName,
        borrowerTaskStatus: BorrowerTaskStatus.Pending,
        linkedDocuments: [],
        allowUpload: false,
        isAgentTask: false,
        allowMarkNA: false,
        signingLinkSentInEmail: null,
        alwaysShowPending: false,
      } as ApplicationTaskModel,

      // OnlineApplication
      {
        taskId: 900010,
        taskTypeId: 'mock-online-app',
        borrowerTaskType: BorrowerTaskType.OnlineApplication,
        description: 'Complete your loan application',
        docType: 'Online Application',
        docSortOrder: 1,
        dueDate: dueInDays(4),
        borrowerId: baseBorrowerId,
        borrowerName: baseBorrowerName,
        borrowerTaskStatus: BorrowerTaskStatus.Pending,
        linkedDocuments: [],
        allowUpload: false,
        isAgentTask: false,
        allowMarkNA: false,
        signingLinkSentInEmail: null,
        alwaysShowPending: false,
      } as ApplicationTaskModel,

      // Rejected task to test visual styling
      {
        taskId: 900011,
        taskTypeId: 'mock-rejected-doc',
        borrowerTaskType: BorrowerTaskType.RequestDocument,
        description: 'Your W-2 was rejected - please re-upload a legible copy',
        docType: 'W-2 Form (Current Year)',
        docSortOrder: 9,
        dueDate: dueInDays(1),
        borrowerId: baseBorrowerId,
        borrowerName: baseBorrowerName,
        borrowerFacingNote: '<b>Rejected:</b> The uploaded document was illegible. Please upload a clear, full-page scan of your W-2.',
        borrowerTaskStatus: BorrowerTaskStatus.Rejected,
        linkedDocuments: [{ fileGuid: 'mock-doc-2', fileName: 'W2_blurry.pdf', extension: 'pdf', dateInserted: new Date() }] as any[],
        allowUpload: true,
        isAgentTask: false,
        allowMarkNA: false,
        signingLinkSentInEmail: null,
        alwaysShowPending: false,
      } as ApplicationTaskModel,
    ];
  }

  /**
   * DEV MOCK: Generates submitted tasks split across two borrowers
   * so the borrower-grouped mobile view can be visually verified.
   */
  private getMockSubmittedTasksForMultipleBorrowers(): ApplicationTaskModel[] {
    const baseBorrowerId = this.application?.myDetails?.borrowerId || 99999;
    const baseBorrowerName = this.application?.myDetails?.borrowerName || 'Sean Tester';
    const coBorrowerId = 88888;
    const coBorrowerName = 'Jane Tester';
    const now = new Date();
    const daysAgo = (days: number) => {
      const d = new Date(now);
      d.setDate(d.getDate() - days);
      return d;
    };

    return [
      // Primary borrower submitted tasks
      {
        taskId: 910001,
        taskTypeId: 'mock-sub-doc-1',
        borrowerTaskType: BorrowerTaskType.RequestDocument,
        description: 'Most recent 2 months of bank statements',
        docType: 'Bank Statements',
        docSortOrder: 1,
        dueDate: daysAgo(5),
        submittedDate: daysAgo(2),
        borrowerId: baseBorrowerId,
        borrowerName: baseBorrowerName,
        borrowerTaskStatus: BorrowerTaskStatus.Submitted,
        linkedDocuments: [],
        allowUpload: false,
        isAgentTask: false,
        allowMarkNA: false,
        signingLinkSentInEmail: null,
        alwaysShowPending: false,
      } as ApplicationTaskModel,
      {
        taskId: 910002,
        taskTypeId: 'mock-sub-doc-2',
        borrowerTaskType: BorrowerTaskType.RequestDocument,
        description: 'W-2 forms for the past 2 years',
        docType: 'W-2 Forms',
        docSortOrder: 2,
        dueDate: daysAgo(7),
        submittedDate: daysAgo(3),
        borrowerId: baseBorrowerId,
        borrowerName: baseBorrowerName,
        borrowerTaskStatus: BorrowerTaskStatus.Submitted,
        linkedDocuments: [],
        allowUpload: false,
        isAgentTask: false,
        allowMarkNA: false,
        signingLinkSentInEmail: null,
        alwaysShowPending: false,
      } as ApplicationTaskModel,
      {
        taskId: 910003,
        taskTypeId: 'mock-sub-doc-3',
        borrowerTaskType: BorrowerTaskType.RequestDocument,
        description: 'Drivers license or government-issued photo ID',
        docType: 'Photo ID',
        docSortOrder: 3,
        dueDate: daysAgo(10),
        approvedDate: daysAgo(1),
        borrowerId: baseBorrowerId,
        borrowerName: baseBorrowerName,
        borrowerTaskStatus: BorrowerTaskStatus.Completed,
        linkedDocuments: [],
        allowUpload: false,
        isAgentTask: false,
        allowMarkNA: false,
        signingLinkSentInEmail: null,
        alwaysShowPending: false,
      } as ApplicationTaskModel,

      // Co-borrower submitted tasks
      {
        taskId: 910004,
        taskTypeId: 'mock-sub-cobor-1',
        borrowerTaskType: BorrowerTaskType.RequestDocument,
        description: 'Most recent 2 months of bank statements',
        docType: 'Bank Statements',
        docSortOrder: 1,
        dueDate: daysAgo(5),
        submittedDate: daysAgo(1),
        borrowerId: coBorrowerId,
        borrowerName: coBorrowerName,
        borrowerTaskStatus: BorrowerTaskStatus.Submitted,
        linkedDocuments: [],
        allowUpload: false,
        isAgentTask: false,
        allowMarkNA: false,
        signingLinkSentInEmail: null,
        alwaysShowPending: false,
      } as ApplicationTaskModel,
      {
        taskId: 910005,
        taskTypeId: 'mock-sub-cobor-2',
        borrowerTaskType: BorrowerTaskType.RequestDocument,
        description: 'Most recent pay stubs (30 days)',
        docType: 'Pay Stubs',
        docSortOrder: 2,
        dueDate: daysAgo(4),
        submittedDate: daysAgo(2),
        borrowerId: coBorrowerId,
        borrowerName: coBorrowerName,
        borrowerTaskStatus: BorrowerTaskStatus.Submitted,
        linkedDocuments: [],
        allowUpload: false,
        isAgentTask: false,
        allowMarkNA: false,
        signingLinkSentInEmail: null,
        alwaysShowPending: false,
      } as ApplicationTaskModel,
      {
        taskId: 910006,
        taskTypeId: 'mock-sub-cobor-3',
        borrowerTaskType: BorrowerTaskType.RequestDocument,
        description: 'Federal tax returns for past 2 years',
        docType: 'Tax Returns',
        docSortOrder: 3,
        dueDate: daysAgo(8),
        submittedDate: daysAgo(3),
        borrowerId: coBorrowerId,
        borrowerName: coBorrowerName,
        borrowerTaskStatus: BorrowerTaskStatus.Submitted,
        linkedDocuments: [],
        allowUpload: false,
        isAgentTask: false,
        allowMarkNA: false,
        signingLinkSentInEmail: null,
        alwaysShowPending: false,
      } as ApplicationTaskModel,
      {
        taskId: 910007,
        taskTypeId: 'mock-sub-cobor-4',
        borrowerTaskType: BorrowerTaskType.RequestDocument,
        description: 'W-2 forms for past 2 years',
        docType: 'W-2 Forms',
        docSortOrder: 4,
        dueDate: daysAgo(7),
        approvedDate: daysAgo(1),
        borrowerId: coBorrowerId,
        borrowerName: coBorrowerName,
        borrowerTaskStatus: BorrowerTaskStatus.Completed,
        linkedDocuments: [],
        allowUpload: false,
        isAgentTask: false,
        allowMarkNA: false,
        signingLinkSentInEmail: null,
        alwaysShowPending: false,
      } as ApplicationTaskModel,
    ];
  }

  private fillApplicationTasks(isAfterDocumentUpload: boolean = false) {

    this.applicationTasks.approved.sort((a, b) => {
      return <any>new Date(a.dueDate) - <any>new Date(b.dueDate);
    });
    this.applicationTasks.requested.sort((a, b) => {
      return <any>new Date(a.dueDate) - <any>new Date(b.dueDate);
    });
    this.applicationTasks.submitted.sort((a, b) => {
      return <any>new Date(a.dueDate) - <any>new Date(b.dueDate);
    });

    const allTasks = this.applicationTasks.requested.concat(this.applicationTasks.submitted).concat(this.applicationTasks.approved);

    Object.assign(this.filteredApplicationTasks, this.applicationTasks);

    if (this.selectedBorrowerId !== 'all') {
      this.filteredApplicationTasks.approved = this.applicationTasks.approved.filter(i => i.borrowerId === Number(this.selectedBorrowerId));
      this.filteredApplicationTasks.requested = this.applicationTasks.requested.filter(i => i.borrowerId === Number(this.selectedBorrowerId));
      this.filteredApplicationTasks.submitted = this.applicationTasks.submitted.filter(i => i.borrowerId === Number(this.selectedBorrowerId));
    }

    const allFilteredTasks = [
      ...(this.filteredApplicationTasks?.requested || []),
      ...(this.filteredApplicationTasks?.submitted || []),
      ...(this.filteredApplicationTasks?.approved || [])
    ];
    this.tasksLoaded.emit({ requested: this.filteredApplicationTasks?.requested || [], all: allFilteredTasks });

    if (this.selectedTab === 1) { //  outstanding
      this.selectedApplicationTasks = this.filteredApplicationTasks.requested;
    }
    else if (this.selectedTab === 2) { //  others
      const allTasks = [
        ...this.filteredApplicationTasks.submitted,
        ...this.filteredApplicationTasks.approved
      ];
      this.selectedApplicationTasks = _.orderBy(allTasks, [t => t.submittedDate ?? t.approvedDate], ['desc']);

    }
    this.groupedTasks = this.groupTasks(this.selectedApplicationTasks);

    // Build borrower-grouped sections for mobile when multiple borrowers exist
    this.hasMultipleBorrowers = this.otherBorrowersOnLoan.length > 0;
    if (this.hasMultipleBorrowers) {
      this.borrowerSections = this.buildBorrowerSections(this.selectedApplicationTasks);
    } else {
      this.borrowerSections = [];
    }

    let taskArr = [];
    this.groupedTasks.forEach(tasks => {
      taskArr = taskArr.concat(...tasks);
    });

    const losEsignTaskToSignFirst = _.sortBy(taskArr, ["taskId"]).find(t => TasksUtils.shouldShowEsignDocument(t, !this.isAgent) && t.borrowerTaskType === BorrowerTaskType.LosEsign);

    if (losEsignTaskToSignFirst) {
      this.losEsignTaskIdToSignFirst = losEsignTaskToSignFirst.taskId;
    }

    if (isAfterDocumentUpload && this.selectedTab !== 2) {
      this._notifsService.showSuccess(
        'Your document(s) have been uploaded successfully.',
        'Upload Complete'
      );
    }
  }

  private filterByAlwaysShowPending(tasks: ApplicationTaskModel[]) {
    return tasks.filter(task => task.alwaysShowPending);
  }

  private filterTaskWithLoanTaskStatus = (isAfterDocumentUpload: boolean = false) => {

    ['approved', 'requested', 'submitted'].forEach(category => {
      let arr = this.applicationTasks[category] as ApplicationTaskModel[];
      arr = this.filterByAlwaysShowPending(arr);
      this.applicationTasks[category] = arr;
    });
    this.fillApplicationTasks(isAfterDocumentUpload);

  }


  private loadScenarios = (isLoadingShowing: boolean = true) => {
    if (isLoadingShowing) {
      this.isLoadingScenarios = true;
    }

    this._portalService.getScenariosForApp(this.appId).subscribe(scenarios => {
      this.scenarios = scenarios;

      // Inject mock scenarios when dev flag is enabled
      if (this.INJECT_MOCK_TASKS) {
        this.scenarios = [...this.scenarios, ...this.getMockScenarios()];
      }

      if (isLoadingShowing) {
        this.isLoadingScenarios = false;
      }
      this._cdr.detectChanges();
    }, err => {
      if (isLoadingShowing) {
        this.isLoadingScenarios = false;
      }
      this._cdr.detectChanges();
      this._notifsService.showError(err && err.error ? err.error.message || err.message : "An error occurred while getting loan scenarios.", 'Error!');
    });
  }

  private allowUpload(task: ApplicationTaskModel): boolean {
    return task.allowUpload && task.borrowerTaskType !== BorrowerTaskType.EsignDocument && task.borrowerTaskType !== BorrowerTaskType.LosEsign;
  }

  private openSignerView = (task: ApplicationTaskModel) => {
    const companyGuid = this._navigationService.companyGuid;
    this._router.navigateByUrl(`/${this.isAgent ? 'agent' : 'borrower'}-portal/loans/${this.appId}/e-sign/${task.taskId}?companyGuid=${companyGuid}`, {
      replaceUrl: true
    });
    return;
  }

  private getLoanConfigs = (silent: boolean = false) => {
    if (!silent) {
      this._spinner.show();
    }
    this._onlineAppService.getConfig(this.appId)
      .subscribe({
        next: (config: FeatureFlags) => {
          this._config = config;
          this.eConsentText = config.eConsentText;
          this.creditAuthText = config.creditAuthText;
          this.voaEnabled = config.voaEnabled;
          this.voeEnabled = config.voeEnabled;
          this.plaidCheckEnabled = config.plaidCheckEnabled;
          this._spinner.hide();
        },
        error: (err) => {
          this._spinner.hide();
          console.error(err);
        }
      })
  }

  /**
   * Generates mock documents for the Document Locker tab.
   */
  private getMockDocuments(): BorrowerDocumentModel[] {
    const now = new Date();
    const daysAgo = (days: number) => {
      const d = new Date(now);
      d.setDate(d.getDate() - days);
      return d;
    };

    return [
      {
        docFileId: 800001,
        guid: 'mock-guid-1',
        title: 'Initial Disclosure Package',
        fileName: 'InitialDisclosure_Signed.pdf',
        mimeType: 'application/pdf',
        dateInserted: daysAgo(2),
        insertedBy: 'System',
        active: true,
      } as BorrowerDocumentModel,
      {
        docFileId: 800002,
        guid: 'mock-guid-2',
        title: 'Loan Estimate',
        fileName: 'LoanEstimate_v2.pdf',
        mimeType: 'application/pdf',
        dateInserted: daysAgo(5),
        insertedBy: 'System',
        active: true,
      } as BorrowerDocumentModel,
      {
        docFileId: 800003,
        guid: 'mock-guid-3',
        title: 'Pre-Approval Letter',
        fileName: 'PreApprovalLetter.pdf',
        mimeType: 'application/pdf',
        dateInserted: daysAgo(10),
        insertedBy: 'Loan Officer',
        active: true,
      } as BorrowerDocumentModel,
      {
        docFileId: 800004,
        guid: 'mock-guid-4',
        title: 'Closing Disclosure',
        fileName: 'ClosingDisclosure_Final.pdf',
        mimeType: 'application/pdf',
        dateInserted: daysAgo(1),
        insertedBy: 'System',
        active: true,
      } as BorrowerDocumentModel,
    ];
  }

  /**
   * Generates mock pricing scenarios for the Scenarios tab.
   */
  private getMockScenarios(): PricingScenario[] {
    return [
      {
        pricingScenarioId: 700001,
        name: 'FHA 30-Year Fixed',
        productName: 'FHA 30 Yr Fixed',
        investor: 'Wells Fargo',
        lockTerm: 30,
        adjustedRate: 6.25,
        adjustedPrice: 100.125,
        apr: 6.89,
        principalAndInterest: 1847.25,
        monthlyMi: 145.00,
        monthlyInsurance: 125.00,
        monthlyTaxes: 275.00,
        totalPayment: 2392.25,
        totalClosingCost: 8750.00,
        shareWithApplicationBorrower: true,
        borrowerNotes: 'Great option for first-time homebuyers with lower down payment.',
        dateInserted: new Date().toISOString(),
      } as any as PricingScenario,
      {
        pricingScenarioId: 700002,
        name: 'Conventional 30-Year Fixed',
        productName: 'Conv 30 Yr Fixed',
        investor: 'Fannie Mae',
        lockTerm: 30,
        adjustedRate: 6.50,
        adjustedPrice: 100.250,
        apr: 6.72,
        principalAndInterest: 1896.20,
        monthlyMi: 0,
        monthlyInsurance: 125.00,
        monthlyTaxes: 275.00,
        totalPayment: 2296.20,
        totalClosingCost: 7200.00,
        shareWithApplicationBorrower: true,
        borrowerNotes: 'No monthly MI with 20% down payment.',
        dateInserted: new Date().toISOString(),
      } as any as PricingScenario,
    ];
  }

  /**
   * Groups tasks by borrower, then by task type within each borrower section.
   * Used on mobile to visually separate tasks per borrower when multiple borrowers exist.
   */
  private buildBorrowerSections(tasks: ApplicationTaskModel[]): { borrowerName: string; borrowerId: number; taskGroups: ApplicationTaskModel[][] }[] {
    const byBorrower = _.groupBy(tasks, t => t.borrowerId || 0);
    const sections: { borrowerName: string; borrowerId: number; taskGroups: ApplicationTaskModel[][] }[] = [];

    // Put current user's tasks first, then other borrowers
    const myBorrowerId = this.application.myDetails?.borrowerId;
    const sortedBorrowerIds = Object.keys(byBorrower).sort((a, b) => {
      if (Number(a) === myBorrowerId) return -1;
      if (Number(b) === myBorrowerId) return 1;
      return 0;
    });

    for (const borrowerId of sortedBorrowerIds) {
      const borrowerTasks = byBorrower[borrowerId];
      const borrowerName = borrowerTasks[0]?.borrowerName || 'Unknown';
      sections.push({
        borrowerName,
        borrowerId: Number(borrowerId),
        taskGroups: this.groupTasks(borrowerTasks)
      });
    }

    return sections;
  }

  private groupTasks = (tasks: ApplicationTaskModel[]): ApplicationTaskModel[][] => {
    return _.values(
      _.groupBy(
        _.sortBy(
          tasks,
          [
            v => !v.isAgentTask,
            v =>
              /**
               * Sort task type 6, 7 and 14 higher than other task types
               * 6: Complete Online Application
               * 7: Asset Verification
               * 14: Esign
               * Order would be 14 then 6 and then 7
               */

              (v.borrowerTaskType === BorrowerTaskType.LosEsign ||
                v.borrowerTaskType === BorrowerTaskType.DigitalAssetVerification ||
                v.borrowerTaskType === BorrowerTaskType.OnlineApplication)
                ? v.borrowerTaskType === BorrowerTaskType.LosEsign
                  ? 0
                  : v.borrowerTaskType === BorrowerTaskType.OnlineApplication
                    ? 1
                    : 2
                : 3,
            v => v.docSortOrder,
          ]
        ),
        (v) => v.taskTypeId
      )
    );
  }
}
